import { useEffect, useRef, useState } from "react";
import { Loader2, Lock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useConfig } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";

const SDK_URL = "https://sdk.mercadopago.com/js/v2";
const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

function loadSdk() {
  if (window.MercadoPago) return Promise.resolve();
  // MP's anti-fraud fingerprint calls (mercadolibre.com/jms) may be CORS-blocked; answer them empty instead of rejecting.
  if (!window.__poFetchPatched) {
    window.__poFetchPatched = true;
    const orig = window.fetch.bind(window);
    window.fetch = (input, init) => {
      const url = typeof input === "string" ? input : input?.url || "";
      const p = orig(input, init);
      return /mercadolibre\.com\/jms|mercadopago\.com\/jms/.test(url) ? p.catch(() => new Response(null, { status: 204 })) : p;
    };
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.onload = resolve;
    s.onerror = () => reject(new Error("Não foi possível carregar o pagamento por cartão."));
    document.body.appendChild(s);
  });
}

const FIELD_STYLE = { "font-size": "18px", color: "#0f172a" };
const fieldBox = "h-[56px] rounded-2xl border-2 border-slate-300 bg-white px-4 pt-[15px]";

export default function CardForm({ onResult }) {
  const { data: cfg } = useConfig();
  const { user } = useAuth();
  const mpRef = useRef(null);
  const keyRef = useRef(newKey());
  const [method, setMethod] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", cpf: "", email: user?.email || "" });

  useEffect(() => {
    if (!cfg?.mercadopago_public_key) return undefined;
    let alive = true;
    const fields = [];
    // MP's anti-fraud fingerprint fetch can fail (CORS) without affecting payment; don't surface it as an app error.
    const swallow = (ev) => { if (String(ev.reason?.message || ev.reason || "").includes("Failed to fetch")) ev.preventDefault(); };
    window.addEventListener("unhandledrejection", swallow);
    loadSdk().then(() => {
      if (!alive) return;
      const mp = new window.MercadoPago(cfg.mercadopago_public_key, { locale: "pt-BR" });
      mpRef.current = mp;
      const num = mp.fields.create("cardNumber", { placeholder: "Número do cartão", style: FIELD_STYLE }).mount("mp-card-number");
      fields.push(num, mp.fields.create("expirationDate", { placeholder: "MM/AA", style: FIELD_STYLE }).mount("mp-expiration"));
      fields.push(mp.fields.create("securityCode", { placeholder: "CVV", style: FIELD_STYLE }).mount("mp-cvv"));
      num.on("binChange", async ({ bin }) => {
        if (!bin) return setMethod(null);
        try {
          const r = await mp.getPaymentMethods({ bin });
          const m = r.results?.[0];
          setMethod(m ? { id: m.id, type: m.payment_type_id, name: m.name } : null);
        } catch (e) { setMethod(null); }
      });
      setReady(true);
    }).catch((e) => toast.error(e.message));
    return () => { alive = false; window.removeEventListener("unhandledrejection", swallow); fields.forEach((x) => { try { x.unmount(); } catch (e) {} }); };
  }, [cfg?.mercadopago_public_key]);

  const pay = async (e) => {
    e.preventDefault();
    if (busy) return;
    const cpf = f.cpf.replace(/\D/g, "");
    if (cpf.length !== 11) return toast.error("Digite um CPF válido (11 números).");
    if (!method) return toast.error("Confira o número do cartão.");
    setBusy(true);
    try {
      const token = await mpRef.current.fields.createCardToken({ cardholderName: f.name.trim(), identificationType: "CPF", identificationNumber: cpf });
      const { data } = await api.post("/pagamentos/cartao", {
        token: token.id, payment_method_id: method.id, payment_method_type: method.type === "debit_card" ? "debit_card" : "credit_card",
        cpf, email: f.email.trim(), idempotency_key: keyRef.current,
      });
      if (data.result === "rejected") keyRef.current = newKey();
      onResult(data);
    } catch (err) {
      toast.error(err.response ? errMsg(err) : "Confira os dados do cartão e tente de novo.");
    }
    setBusy(false);
  };

  const input = (k, label, props = {}) => (
    <div>
      <label className="po-label" htmlFor={`card-${k}`}>{label}</label>
      <input id={`card-${k}`} className="po-input" required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`card-${k}-input`} {...props} />
    </div>
  );

  if (!cfg?.mercadopago_public_key) return <p className="po-card text-lg">Pagamento por cartão ainda não configurado.</p>;
  return (
    <form onSubmit={pay} className="po-card space-y-4" data-testid="card-form">
      <p className="flex items-center gap-2 text-xl font-bold text-slate-900"><CreditCard className="h-6 w-6 text-orange-500" /> Cartão de crédito ou débito</p>
      <div><p className="po-label">Número do cartão</p><div id="mp-card-number" className={fieldBox} data-testid="card-number-field" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><p className="po-label">Validade</p><div id="mp-expiration" className={fieldBox} data-testid="card-expiration-field" /></div>
        <div><p className="po-label">CVV</p><div id="mp-cvv" className={fieldBox} data-testid="card-cvv-field" /></div>
      </div>
      {method && <p className="text-base text-slate-600" data-testid="card-brand">{method.name} · {method.type === "debit_card" ? "Débito" : "Crédito"} · 1x de R$ 11,99</p>}
      {input("name", "Nome do titular (como no cartão)", { autoComplete: "cc-name" })}
      {input("cpf", "CPF do titular", { inputMode: "numeric", maxLength: 14, placeholder: "000.000.000-00" })}
      {input("email", "E-mail", { type: "email", autoComplete: "email" })}
      <button type="submit" disabled={busy || !ready} className="po-btn-primary min-h-[68px] w-full text-xl" data-testid="card-pay-button">
        {busy ? <Loader2 className="h-7 w-7 animate-spin" /> : <Lock className="h-6 w-6" />} {busy ? "Processando..." : "Pagar R$ 11,99"}
      </button>
      <p className="flex items-center gap-2 text-sm text-slate-500"><Lock className="h-4 w-4" /> Os dados do cartão vão direto para o Mercado Pago. Nós não vemos nem guardamos o número do seu cartão.</p>
    </form>
  );
}
